

import { defineComponent, PropType, shallowRef } from "vue";
import mapboxgl from 'mapbox-gl';
import moment from "moment";
import { mapStores } from "pinia";

import PollutionLayerHyperLocalManager from './pollution-layer-hyperlocal-manager.vue';
import PollutionLayerStationManager from './pollution-layer-station-manager.vue';
import InformationModal from './information-modal.vue';

import { Hyperlocal } from "../../api/models/hyperlocal.model";
import { Station } from "../../api/models/station.model";
import { useMapStore } from "../../store/map.store";

const accessToken =
  'pk.eyJ1IjoiamlsbGNoZXZhbGllcmVsaWNoZW5zIiwiYSI6ImNrYThhcmFkMTBiZTEydHJ1Ym1idDd1dDkifQ.NUC-TyqKFc9X13uaf52ENg';

export default defineComponent({

    components: {
        PollutionLayerHyperLocalManager,
        PollutionLayerStationManager,
        InformationModal,
    },

    props: {
        stations: { type: Object as PropType<Station[]>},
        currentStation: { type: Object as PropType<Station>},
    },

    data() {
        const now = moment();
        return {
            map: null,
            baseUrl: `https://storage.googleapis.com/elichens-aqmap/tiles`,
            ready: false,
        }
    },

    computed: {
        ...mapStores(useMapStore),

        tileUrl(): string {
            const now = moment();
            return `${this.baseUrl}/world/${now.format('YYYY/MM/DD/HH')}/all/{z}/{x}/{y}.png`;
        },

        hyperlocals(): Hyperlocal[] {
            return this.mapStore.hyperLocalInBounds;
        },

        bounds(): mapboxgl.LngLatBounds | null {
            
            const stations = this.stations?.filter((station: Station) => station.location?.lat && station.location?.lon);

            if(!stations || !stations.length) {
                return new mapboxgl.LngLatBounds([{ lat: 45.10733, lon: 5.63143}, { lat: 45.24805 , lon: 5.81513}]); // grenoble
            }

            let bounds: mapboxgl.LngLatBounds | null = new mapboxgl.LngLatBounds();
            stations.forEach((station: Station) => {
                bounds = bounds.extend(new mapboxgl.LngLat(station.location.lon, station.location.lat));
            });
            return bounds;
        }
    },

    async mounted(){
        await this.mapStore.fetchHyperlocals();
        mapboxgl.accessToken = accessToken;

        if(!this.$refs.map) {
            return;
        }

        this.map = shallowRef(new mapboxgl.Map({
            container: this.$refs.map as HTMLElement,
            style: "mapbox://styles/jillchevalierelichens/ckcer3ql8176i1inoy9ytslt6",
            // sprite: "mapbox://sprites/jillchevalierelichens/ckcer3ql8176i1inoy9ytslt6",
            bounds: this.bounds,
            maxZoom: 15,
            bearing: 0,
            attributionControl: false, // removes attribution and keeps the logo
        }));

        this.map.on('load', ()=> {
            this.display_copernicus();
            this.map.resize();
            this.initEvents();
            this.fitBounds();
            this.mapStore.bounds = this.map.getBounds();
            this.mapStore.zoom = this.map.getZoom();
            this.map.resize();
            this.ready = true;
        });

        // this.map.doubleClickZoom.disabled();
        this.map.on('dblclick',(event) => {
            this.$emit('aqmap:dblclick', event.lngLat);
        })
    },

    methods: {
        initEvents(): void {
            this.map.on('moveend', (e) => this.mapMoved(e));

            this.map.on('error', e => {
                // Hide those annoying non-error errors
                if (e && (e?.message !== 'Error: Not Found' && !e?.error?.url?.includes('storage.googleapis')))
                    console.error(e);
            });
        },

        resize(): void {
            if(this.map) {
                this.map.resize();
            }
        },

        mapMoved(e): void {
            if(!this.ready) {
                return;
            }

            this.mapStore.bounds = this.map.getBounds();
            this.mapStore.zoom = this.map.getZoom();
        },

        fitBounds(): void {
            if(!this.map) {
                return;
            }

            this.map.fitBounds(this.bounds, {duration: 0});
            this.map.setZoom(this.map.getZoom() - 1);
        },

        display_copernicus() {

          this.map.addSource("copernicus", {
            type: "raster",
            tiles: [this.tileUrl],
            minzoom: 0,
            maxzoom: 3,
            scheme: "tms",
          });

          this.map.addLayer(
            {
              id: "copernicus",
              source: "copernicus",
              type: "raster",
              paint: {
                // opacity of the tiles decrease when zooming into the map to see hyper-local
                "raster-opacity": [
                  "interpolate",
                  ["linear"],
                  ["zoom"],
                  9,
                  0.55,
                  13,
                  0.4,
                ],
                // 'raster-opacity': 0.55
              },
            },
            "airport-label"
          );
        },

        openInformation(): void {
            if(!this.$refs.infoModal) {
                return;
            }

            (this.$refs.infoModal as typeof InformationModal).open();
        }
    },

    watch: {
        currentStation(station: Station): void {
            if(!station || !this.ready) {
                return;
            }


            this.map.flyTo({
                center: [ station.location.lon, station.location.lat],
                zoom: 12,
                essential: true // this animation is considered essential with respect to prefers-reduced-motion
            });
        }
    }
})

